import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    IconButton,
    Tooltip,
    TextField,
    MenuItem,
    CircularProgress,
    Tabs,
    Tab,
    Button,
    Divider,
    LinearProgress,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import {
    getUserAnalyticsDashboard,
    getUserAnalyticsList,
    getUserAnalyticsById,
    getUserJourney,
} from "../../../api/services/api-typed";
import PeopleIcon from "@mui/icons-material/People";
import TimelineIcon from "@mui/icons-material/Timeline";
import DevicesIcon from "@mui/icons-material/Devices";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import PageviewIcon from "@mui/icons-material/Pageview";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import {
    EnhancedAnalyticsCard,
    PageLayout,
    CardSkeleton,
} from "../../../components/Enhanced";

// Tab Panel component
function TabPanel(props: {
    children?: React.ReactNode;
    index: number;
    value: number;
}) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

export const UserAnalyticsDashboard = () => {
    const [tabValue, setTabValue] = useState(0);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [platformFilter, setPlatformFilter] = useState<string>("");
    const [userTypeFilter, setUserTypeFilter] = useState<string>("");
    const [dateRange, setDateRange] = useState({ start: "", end: "" });

    const queryParams = {
        ...(platformFilter && { platform: platformFilter }),
        ...(userTypeFilter && { user_type: userTypeFilter }),
        ...(dateRange.start && { start_date: dateRange.start }),
        ...(dateRange.end && { end_date: dateRange.end }),
    };

    // Dashboard overview
    const {
        data: dashboard,
        isLoading: dashboardLoading,
        refetch: refetchDashboard,
    } = useQuery(["user-analytics-dashboard", queryParams], () =>
        getUserAnalyticsDashboard(queryParams)
    );

    // Users list
    const {
        data: usersList,
        isLoading: usersLoading,
        refetch: refetchUsers,
    } = useQuery(["user-analytics-list", queryParams], () =>
        getUserAnalyticsList({ ...queryParams, limit: 50 })
    );

    // Selected user details
    const { data: userDetails, isLoading: userDetailsLoading } = useQuery(
        ["user-analytics-detail", selectedUserId],
        () => getUserAnalyticsById(selectedUserId!, {}),
        { enabled: !!selectedUserId }
    );

    // Selected user journey
    const { data: userJourneyData, isLoading: journeyLoading } = useQuery(
        ["user-journey", selectedUserId],
        () => getUserJourney(selectedUserId!, { days: 30, limit: 200 }),
        { enabled: !!selectedUserId }
    );

    const dashboardData = dashboard?.data || dashboard;
    const usersListData = usersList?.data || usersList;
    const journeyData = userJourneyData?.data || userJourneyData;

    const handleRefreshAll = () => {
        refetchDashboard();
        refetchUsers();
    };

    const platformColors: Record<string, string> = {
        user_web: "#4caf50",
        clinician_web: "#2196f3",
        admin_web: "#ff9800",
        user_app: "#9c27b0",
        clinician_app: "#e91e63",
    };

    const formatDate = (date: string) =>
        new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });

    // User Detail View
    if (selectedUserId) {
        return (
            <PageLayout
                title="User Behavior Analysis"
                subtitle={`Tracking user #${selectedUserId}'s journey and behavior`}
                breadcrumbs={[
                    { label: "Analytics", path: "/analytics" },
                    { label: "User Behavior", path: "/user-analytics" },
                    { label: `User #${selectedUserId}` },
                ]}
                actions={[
                    {
                        label: "Back to Users",
                        onClick: () => setSelectedUserId(null),
                        variant: "outlined",
                        icon: <ArrowBackIcon />,
                    },
                ]}
            >
                {userDetailsLoading || journeyLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* User Journey Summary Cards */}
                        <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} sm={6} md={3}>
                                <EnhancedAnalyticsCard
                                    title="Total Events"
                                    value={journeyData?.totalEvents || 0}
                                    icon={<TouchAppIcon />}
                                    color="primary"
                                    tooltip="Total tracked events for this user"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <EnhancedAnalyticsCard
                                    title="Sessions"
                                    value={journeyData?.totalSessions || 0}
                                    icon={<DevicesIcon />}
                                    color="secondary"
                                    tooltip="Browser sessions"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <EnhancedAnalyticsCard
                                    title="Pages Visited"
                                    value={journeyData?.uniquePagesVisited || 0}
                                    icon={<PageviewIcon />}
                                    color="success"
                                    tooltip="Unique pages visited"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <EnhancedAnalyticsCard
                                    title="Event Types"
                                    value={
                                        Object.keys(journeyData?.eventBreakdown || {}).length
                                    }
                                    icon={<EventIcon />}
                                    color="warning"
                                    tooltip="Distinct event types triggered"
                                />
                            </Grid>
                        </Grid>

                        {/* Event Breakdown */}
                        <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" mb={2} fontWeight={600}>
                                            Event Breakdown
                                        </Typography>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            {Object.entries(journeyData?.eventBreakdown || {})
                                                .sort(
                                                    ([, a]: any, [, b]: any) =>
                                                        (b as number) - (a as number)
                                                )
                                                .map(([name, count]: any) => (
                                                    <Box
                                                        key={name}
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        p={1.5}
                                                        sx={{
                                                            backgroundColor: "grey.50",
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {name.replace(/_/g, " ")}
                                                        </Typography>
                                                        <Chip
                                                            label={count}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    </Box>
                                                ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" mb={2} fontWeight={600}>
                                            Top Pages
                                        </Typography>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            {(journeyData?.topPages || []).map(
                                                (page: string, idx: number) => (
                                                    <Box
                                                        key={idx}
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                        p={1.5}
                                                        sx={{
                                                            backgroundColor: "grey.50",
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                                borderRadius: "50%",
                                                                backgroundColor: "primary.main",
                                                                color: "white",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {idx + 1}
                                                        </Box>
                                                        <Typography variant="body2">{page}</Typography>
                                                    </Box>
                                                )
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* User Timeline */}
                        <Card elevation={2}>
                            <CardContent>
                                <Typography variant="h6" mb={2} fontWeight={600}>
                                    Activity Timeline (Last 30 Days)
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Time</TableCell>
                                                <TableCell>Event</TableCell>
                                                <TableCell>Page</TableCell>
                                                <TableCell>Platform</TableCell>
                                                <TableCell>Details</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(journeyData?.timeline || [])
                                                .slice(0, 100)
                                                .map((event: any, idx: number) => (
                                                    <TableRow key={idx} hover>
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            <Typography variant="caption">
                                                                {formatDate(event.createdAt)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={event.eventName.replace(/_/g, " ")}
                                                                size="small"
                                                                variant="outlined"
                                                                color={
                                                                    event.eventName === "page_view"
                                                                        ? "primary"
                                                                        : event.eventName.includes("login")
                                                                            ? "success"
                                                                            : "default"
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {event.pagePath || "-"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={event.platform}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor:
                                                                        platformColors[event.platform] || "#999",
                                                                    color: "white",
                                                                    fontSize: 11,
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ maxWidth: 200, display: "block" }}
                                                                noWrap
                                                            >
                                                                {event.eventData
                                                                    ? JSON.stringify(event.eventData).substring(
                                                                        0,
                                                                        80
                                                                    )
                                                                    : "-"}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </>
                )}
            </PageLayout>
        );
    }

    // Main Dashboard View
    return (
        <PageLayout
            title="User Behavior Analytics"
            subtitle="Track user activity, engagement, and behavior across all platforms"
            breadcrumbs={[
                { label: "Dashboard", path: "/dashboard" },
                { label: "User Analytics" },
            ]}
            actions={[
                {
                    label: "Refresh",
                    onClick: handleRefreshAll,
                    variant: "outlined",
                    icon: <RefreshIcon />,
                },
            ]}
        >
            {/* Filters */}
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                <TextField
                    select
                    label="Platform"
                    value={platformFilter}
                    onChange={(e) => setPlatformFilter(e.target.value)}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">All Platforms</MenuItem>
                    <MenuItem value="user_web">User Web</MenuItem>
                    <MenuItem value="clinician_web">Clinician Web</MenuItem>
                    <MenuItem value="admin_web">Admin Web</MenuItem>
                    <MenuItem value="user_app">User App</MenuItem>
                    <MenuItem value="clinician_app">Clinician App</MenuItem>
                </TextField>
                <TextField
                    select
                    label="User Type"
                    value={userTypeFilter}
                    onChange={(e) => setUserTypeFilter(e.target.value)}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="PATIENT">Patients</MenuItem>
                    <MenuItem value="CLINICIAN">Clinicians</MenuItem>
                    <MenuItem value="ADMIN">Admins</MenuItem>
                </TextField>
                <TextField
                    type="date"
                    label="From"
                    value={dateRange.start}
                    onChange={(e) =>
                        setDateRange({ ...dateRange, start: e.target.value })
                    }
                    size="small"
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    type="date"
                    label="To"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                />
            </Box>

            <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                sx={{ mb: 2 }}
            >
                <Tab label="Overview" />
                <Tab label="Users" />
                <Tab label="Live Feed" />
            </Tabs>

            {/* Tab 0: Overview */}
            <TabPanel value={tabValue} index={0}>
                {dashboardLoading ? (
                    <Grid container spacing={3}>
                        {[1, 2, 3, 4].map((i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <CardSkeleton variant="analytics" count={1} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} sm={6} md={3}>
                                <EnhancedAnalyticsCard
                                    title="Total Events"
                                    value={dashboardData?.summary?.totalEvents || 0}
                                    icon={<TimelineIcon />}
                                    color="primary"
                                    tooltip="Total analytics events tracked"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <EnhancedAnalyticsCard
                                    title="Active Platforms"
                                    value={
                                        dashboardData?.summary?.eventsByPlatform?.length || 0
                                    }
                                    icon={<DevicesIcon />}
                                    color="secondary"
                                    tooltip="Number of platforms sending data"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <EnhancedAnalyticsCard
                                    title="Top Users"
                                    value={dashboardData?.topUsers?.length || 0}
                                    icon={<PeopleIcon />}
                                    color="success"
                                    tooltip="Most active users"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <EnhancedAnalyticsCard
                                    title="Event Types"
                                    value={dashboardData?.eventBreakdown?.length || 0}
                                    icon={<TrendingUpIcon />}
                                    color="warning"
                                    tooltip="Distinct event types tracked"
                                />
                            </Grid>
                        </Grid>

                        {/* Platform Breakdown & Event Types */}
                        <Grid container spacing={3} mb={4}>
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" mb={2} fontWeight={600}>
                                            Events by Platform
                                        </Typography>
                                        <Box display="flex" flexDirection="column" gap={1.5}>
                                            {(dashboardData?.summary?.eventsByPlatform || []).map(
                                                (p: any) => (
                                                    <Box
                                                        key={p.platform}
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        p={2}
                                                        sx={{
                                                            backgroundColor: "grey.50",
                                                            borderRadius: 1,
                                                            borderLeft: `4px solid ${platformColors[p.platform] || "#999"
                                                                }`,
                                                        }}
                                                    >
                                                        <Typography fontWeight={500}>
                                                            {p.platform.replace(/_/g, " ").toUpperCase()}
                                                        </Typography>
                                                        <Chip
                                                            label={p.count.toLocaleString()}
                                                            sx={{
                                                                backgroundColor:
                                                                    platformColors[p.platform] || "#999",
                                                                color: "white",
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </Box>
                                                )
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" mb={2} fontWeight={600}>
                                            Top Event Types
                                        </Typography>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            {(dashboardData?.eventBreakdown || [])
                                                .slice(0, 10)
                                                .map((e: any) => {
                                                    const maxCount = Math.max(
                                                        ...(dashboardData?.eventBreakdown || []).map(
                                                            (ev: any) => ev.count
                                                        ),
                                                        1
                                                    );
                                                    return (
                                                        <Box key={e.eventName}>
                                                            <Box
                                                                display="flex"
                                                                justifyContent="space-between"
                                                                mb={0.5}
                                                            >
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {e.eventName.replace(/_/g, " ")}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {e.count.toLocaleString()}
                                                                </Typography>
                                                            </Box>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={(e.count / maxCount) * 100}
                                                                sx={{ height: 6, borderRadius: 3 }}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Top Pages */}
                        <Card elevation={2} sx={{ mb: 4 }}>
                            <CardContent>
                                <Typography variant="h6" mb={2} fontWeight={600}>
                                    Most Visited Pages
                                </Typography>
                                <Grid container spacing={2}>
                                    {(dashboardData?.topPages || [])
                                        .slice(0, 10)
                                        .map((p: any, idx: number) => (
                                            <Grid item xs={12} sm={6} key={idx}>
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    p={1.5}
                                                    sx={{
                                                        backgroundColor: "grey.50",
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Box
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: "50%",
                                                                backgroundColor: "primary.main",
                                                                color: "white",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {idx + 1}
                                                        </Box>
                                                        <Typography variant="body2" noWrap>
                                                            {p.pagePath}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={`${p.views} views`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Grid>
                                        ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </>
                )}
            </TabPanel>

            {/* Tab 1: Users List */}
            <TabPanel value={tabValue} index={1}>
                {usersLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" mb={2} fontWeight={600}>
                                Tracked Users ({usersListData?.total || 0})
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>User</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell align="center">Total Events</TableCell>
                                            <TableCell>First Seen</TableCell>
                                            <TableCell>Last Active</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(usersListData?.data || []).map((row: any) => (
                                            <TableRow key={row.userId} hover>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1.5}>
                                                        <Avatar
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                backgroundColor:
                                                                    row.userType === "PATIENT"
                                                                        ? "primary.main"
                                                                        : row.userType === "CLINICIAN"
                                                                            ? "secondary.main"
                                                                            : "warning.main",
                                                            }}
                                                        >
                                                            <PersonIcon sx={{ fontSize: 20 }} />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {row.user?.full_name || `User #${row.userId}`}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {row.user?.email || ""}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={row.userType}
                                                        size="small"
                                                        color={
                                                            row.userType === "PATIENT"
                                                                ? "primary"
                                                                : row.userType === "CLINICIAN"
                                                                    ? "secondary"
                                                                    : "warning"
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography fontWeight={600}>
                                                        {row.totalEvents?.toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        {row.firstSeen ? formatDate(row.firstSeen) : "-"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        {row.lastSeen ? formatDate(row.lastSeen) : "-"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="View User Journey">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => setSelectedUserId(row.userId)}
                                                        >
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}
            </TabPanel>

            {/* Tab 2: Live Feed */}
            <TabPanel value={tabValue} index={2}>
                {dashboardLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" mb={2} fontWeight={600}>
                                Recent Activity Feed
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Time</TableCell>
                                            <TableCell>User</TableCell>
                                            <TableCell>Event</TableCell>
                                            <TableCell>Page</TableCell>
                                            <TableCell>Platform</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(dashboardData?.recentEvents || []).map(
                                            (event: any, idx: number) => (
                                                <TableRow key={idx} hover>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                        <Typography variant="caption">
                                                            {formatDate(event.createdAt)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                            sx={{ cursor: "pointer" }}
                                                            onClick={() =>
                                                                setSelectedUserId(event.userId)
                                                            }
                                                        >
                                                            <Avatar
                                                                sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    fontSize: 12,
                                                                    backgroundColor:
                                                                        event.userType === "PATIENT"
                                                                            ? "primary.main"
                                                                            : "secondary.main",
                                                                }}
                                                            >
                                                                {event.user?.full_name?.charAt(0) || "?"}
                                                            </Avatar>
                                                            <Typography variant="body2">
                                                                {event.user?.full_name || `#${event.userId}`}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={event.eventName.replace(/_/g, " ")}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap>
                                                            {event.pagePath || "-"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={event.platform}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor:
                                                                    platformColors[event.platform] || "#999",
                                                                color: "white",
                                                                fontSize: 11,
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}
            </TabPanel>
        </PageLayout>
    );
};
